import { createContext, useEffect }  from 'react';
import { observer } from 'mobx-react-lite';

import { UserProps } from 'stores/user_store';
import useStores from 'hooks/useStores';
import useAuth from 'hooks/useAuth';


export type UserContextType = {
    updateField: (field: string, value: any) => Promise<UserProps>;
    supportEmailSearch: (identity: string, query: string) => Promise<UserProps[]>;
    supportUserFetch: (identity: string, userKey: string) => Promise<UserProps>;
    sudoSetAllowedIdentities: (userKey: string, allowedIdentities: number, identity: string) => Promise<UserProps>;
    supportAssignableUsers: (workKey: string) => Promise<UserProps[]>;
    managerUserCreate: (workKey: string,
        primary_email: string,
        stripe_customer_id: string,
        recurly_account_code: string,
        subscription_service: string,
        subscription_product: string) => Promise<UserProps>;
    sudoUpdateICStatus: (workKey: string, user: string, is_ic: boolean) => Promise<UserProps>;
    sudoUpdateIsBeta: (workKey: string, user: string, is_beta: boolean) => Promise<UserProps>;
    sudoUpdateIsDisabled: (workKey: string, user: string, is_disabled: boolean) => Promise<UserProps>;

    managerSetSubscriptionService: (workKey: string, user: string, subscription_service: string) => Promise<UserProps>;
    managerSetSubscriptionProduct: (workKey: string, user: string, subscription_product: string) => Promise<UserProps>;
    managerSetSubscriptionCustomerId: (workKey: string, user: string, subscription_customer_id: string) => Promise<UserProps>;
    managerSetRecurlyIncludedMinutes: (workKey: string, user: string, minutes: number) => Promise<UserProps>;

    signupEmailStart: (email: string) => Promise<boolean>;

    //signupEmailFinish: (email: string, code: string, password: string) => Promise<UserProps>;

    knownUsers: UserProps[];
};

const UserContext = createContext<UserContextType>({
    updateField: () => { throw "not implemented" },
    supportEmailSearch: () => { throw "not implemented" },
    supportUserFetch: () => { throw "not implemented" },
    sudoSetAllowedIdentities: () => { throw "not implemented" },
    supportAssignableUsers: () => { throw "not implemented" },
    managerUserCreate: () => { throw "not implemented" },
    sudoUpdateICStatus: () => { throw "not implemented" },
    sudoUpdateIsBeta: () => { throw "not implemented" },
    sudoUpdateIsDisabled: () => { throw "not implemented" },
    signupEmailStart: () => { throw "not implemented" },

    managerSetSubscriptionService: () => { throw "not implemented" },
    managerSetSubscriptionProduct: () => { throw "not implemented" },
    managerSetSubscriptionCustomerId: () => { throw "not implemented" },
    managerSetRecurlyIncludedMinutes: () => { throw "not implemented" },

    knownUsers: [],
});


const UserProvider = observer(({ children }: { children: React.ReactNode  })=> {
    const { userStore } = useStores();
    const { currentUser, updateCurrentUser } = useAuth();

    // Listen for updates to user objects, which are almost certainly
    // our users objects. If so, send them to the auth context. It
    // most likely means our minutes/credits amount changed.
    useEffect(() => {
        console.log("userStore.items", userStore.items.length);
        if (userStore.items.length > 0) {
            const u = userStore.items[userStore.items.length - 1];
            // we got an update for our user, tell auth
            if(u.uuid == currentUser.uuid || u.key == currentUser.key) {
                updateCurrentUser(u);
            }
        }
    }, [userStore.items]);

    const updateField = async (field: string, value: any) => {
        return userStore.updateField(field, value).then((res: UserProps) => {
            return res;
        });
    };

    const supportEmailSearch = async (identity: string, query: string) => {
        return userStore.supportEmailSearch(identity, query).then((res: UserProps[]) => {
            return res;
        });
    };

    const supportUserFetch = async (identity: string, userKey: string) => {
        return userStore.supportUserFetch(identity, userKey).then((res: UserProps) => {
            return res;
        });
    };

    const supportAssignableUsers = async (workKey: string) => {
        return userStore.supportAssignableUsers(workKey).then((res: UserProps[]) => {
            return res;
        });
    };

    const sudoSetAllowedIdentities = async (userKey: string, allowedIdentities: number, identity: string) => {
        return userStore.sudoSetAllowedIdentities(userKey, allowedIdentities, identity).then((res: UserProps) => {
            return res;
        });
    };


    const signupEmailStart = async (email: string) => {
        return userStore.signupEmailStart(email)
            .then((res: boolean) => {
                return res;
            });
    };

    const sudoUpdateIsBeta = async (workKey: string, user: string, is_beta: boolean) => {
        return userStore.sudoUpdateIsBeta(workKey, user, is_beta).then((res: UserProps) => {
            return res;
        });
    };
    const sudoUpdateIsDisabled = async (workKey: string, user: string, is_disabled: boolean) => {
        return userStore.sudoUpdateIsDisabled(workKey, user, is_disabled).then((res: UserProps) => {
            return res;
        });
    };


    const managerSetSubscriptionService = async (workKey: string, user: string, subscription_service: string) => {
        return userStore.managerSetSubscriptionService(workKey, user, subscription_service).then((res: UserProps) => {
            return res;
        });
    };

    const managerSetSubscriptionProduct = async (workKey: string, user: string, subscription_product: string) => {
        return userStore.managerSetSubscriptionProduct(workKey, user, subscription_product).then((res: UserProps) => {
            return res;
        });
    };

    const managerSetSubscriptionCustomerId = async (workKey: string, user: string, subscription_customer_id: string) => {
        return userStore.managerSetSubscriptionCustomerId(workKey, user, subscription_customer_id).then((res: UserProps) => {
            return res;
        });
    };

    const managerSetRecurlyIncludedMinutes = async (workKey: string, user: string, minutes: number) => {
        return userStore.managerSetRecurlyIncludedMinutes(workKey, user, minutes).then((res: UserProps) => {
            return res;
        });
    };


    const data = {
        updateField,
        supportEmailSearch: supportEmailSearch,
        supportUserFetch: supportUserFetch,
        sudoSetAllowedIdentities: sudoSetAllowedIdentities,
        knownUsers: userStore.items,
        supportAssignableUsers: supportAssignableUsers,
        managerUserCreate: userStore.managerUserCreate,
        sudoUpdateICStatus: userStore.sudoUpdateICStatus,
        signupEmailStart: signupEmailStart,
        sudoUpdateIsBeta: sudoUpdateIsBeta,
        sudoUpdateIsDisabled: sudoUpdateIsDisabled,

        managerSetSubscriptionService: managerSetSubscriptionService,
        managerSetSubscriptionProduct: managerSetSubscriptionProduct,
        managerSetSubscriptionCustomerId: managerSetSubscriptionCustomerId,
        managerSetRecurlyIncludedMinutes: managerSetRecurlyIncludedMinutes,
    }
    return (
        <UserContext.Provider value={data}>{ children }</UserContext.Provider>
    )

});

export { UserContext, UserProvider };
// import React from 'react';
// import { UserProps } from '../stores/user_store';
// export const userContext = React.createContext<UserProps>({ key: ""} as UserProps);
